import { useMemo } from 'react';
import { graphql } from 'react-relay';

import type { StaticContext } from '@atlassian/forge-conditions';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getIsSiteAdminFromQuery } from '@confluence/current-user/entry-points/getIsSiteAdminFromQuery';
import { getIsProductAdminFromQuery } from '@confluence/current-user/entry-points/getIsProductAdminFromQuery';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import { forgeGlobalExtensions } from '../ForgeModuleType';
import type { ForgeModules } from '../ForgeModules';

import type {
	useStaticContextDisplayConditionsSpaceAdminQuery$data,
	useStaticContextDisplayConditionsSpaceAdminQuery$variables,
} from './__generated__/useStaticContextDisplayConditionsSpaceAdminQuery.graphql';
import type {
	useStaticContextUserPrivilegeQuery$data,
	useStaticContextUserPrivilegeQuery$variables,
} from './__generated__/useStaticContextUserPrivilegeQuery.graphql';
import type {
	useStaticContextOperationsQuery$data,
	useStaticContextOperationsQuery$variables,
} from './__generated__/useStaticContextOperationsQuery.graphql';

const UseStaticContextDisplayConditionsSpaceAdminQuery = graphql`
	query useStaticContextDisplayConditionsSpaceAdminQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			currentUser {
				isAdmin
			}
		}
	}
`;

const UseStaticContextUserPrivilegeQuery = graphql`
	query useStaticContextUserPrivilegeQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				operations {
					operation
					targetType
				}
			}
		}
		isSiteAdmin
		hasUserAccessAdminRole
	}
`;

const UseStaticContextOperationsQuery = graphql`
	query useStaticContextOperationsQuery {
		siteOperations {
			userProfile
			application
		}
	}
`;

export interface UseStaticContextHookResult {
	staticContext?: StaticContext;
	loading: boolean;
	error?: Error;
}

export interface UseStaticContextProps {
	moduleType: ForgeModules;
	spaceKey?: string;
}

export function useStaticContext({
	moduleType,
	spaceKey,
}: UseStaticContextProps): UseStaticContextHookResult {
	const { isLoggedIn, userId } = useSessionData();

	const isGlobalExtension = forgeGlobalExtensions.includes(moduleType);

	const {
		data: spaceAdminData,
		loading: spaceAdminLoading,
		error: spaceAdminError,
	} = useQueryInterop<
		useStaticContextDisplayConditionsSpaceAdminQuery$data,
		useStaticContextDisplayConditionsSpaceAdminQuery$variables
	>(UseStaticContextDisplayConditionsSpaceAdminQuery, {
		variables: {
			spaceKey: spaceKey ?? '',
		},
		skip: !spaceKey || isGlobalExtension,
	});

	const {
		data: operationData,
		loading: operationLoading,
		error: operationError,
	} = useQueryInterop<
		useStaticContextOperationsQuery$data,
		useStaticContextOperationsQuery$variables
	>(UseStaticContextOperationsQuery, {
		skip: !userId,
	});
	let canViewUserProfiles = false;

	if (operationData) {
		canViewUserProfiles = operationData?.siteOperations?.userProfile?.includes('read') ?? false;
	}
	const {
		data: privilegeData,
		loading: privilegeLoading,
		error: privilegeError,
	} = useQueryInterop<
		useStaticContextUserPrivilegeQuery$data,
		useStaticContextUserPrivilegeQuery$variables
	>(UseStaticContextUserPrivilegeQuery, {
		skip: operationLoading || !!operationError || !canViewUserProfiles,
	});
	const isSpaceAdmin = spaceAdminData?.space?.currentUser?.isAdmin;
	const isSiteAdmin = getIsSiteAdminFromQuery(privilegeData);
	const isAdmin = isSiteAdmin || getIsProductAdminFromQuery(privilegeData);
	const staticContext = useMemo(
		() => ({
			isSpaceAdmin,
			isLoggedIn,
			isAdmin,
			isSiteAdmin,
		}),
		[isSpaceAdmin, isLoggedIn, isAdmin, isSiteAdmin],
	);

	if (spaceAdminLoading || spaceAdminError) {
		return { loading: spaceAdminLoading, error: spaceAdminError };
	}

	if (operationLoading || operationError) {
		return { loading: operationLoading, error: operationError };
	}

	if (privilegeLoading || privilegeError) {
		return { loading: privilegeLoading, error: privilegeError };
	}

	return {
		staticContext,
		loading: false,
	};
}
